import { items } from '../../shared/Items'

export class ClientProjectiles {
  constructor(world) {
    this.world = world
    this._active = []
  }

  /**
   * Spawn a projectile sprite travelling from (sx, sy) to (ex, ey).
   * Reads sprite/impact/speed from item.use (projectile type).
   */
  spawn(data) {
    const {
      sx,
      sy,
      pdx,
      pdy,
      maxDist,
      ex,
      ey,
      delay = 0,
      flightSprite,
      impactSprite,
      item = null,
      itemId = null,
      shooter = null,
    } = data
    const resolvedItem = item || items[itemId]
    const resolvedProj = resolvedItem?.use?.projectiles?.[0]
    const sprite = flightSprite ?? resolvedProj?.flightSprite
    const impact = impactSprite ?? resolvedProj?.impactSprite
    const arc = data.arc ?? resolvedProj?.arc ?? 0
    const speed = data.speed ?? resolvedProj?.speed ?? 10
    const projRadius = data.projRadius ?? resolvedProj?.radius ?? 0.15
    const maxTargets = data.maxTargets ?? resolvedProj?.maxTargets ?? 1
    if (!sprite?.url) return

    // If no direction provided, derive from start/end
    let dx = pdx,
      dy = pdy,
      dist = maxDist
    if (dx == null) {
      dx = ex - sx
      dy = ey - sy
      dist = Math.sqrt(dx * dx + dy * dy)
      if (dist < 0.01) return
      dx /= dist
      dy /= dist
    }

    const shadowSprite = data.shadowSprite ?? resolvedProj?.shadowSprite

    const doSpawn = () => {
      const s = this.world.sprites.add({
        url: sprite.url,
        fps: sprite.fps,
        width: 1,
        height: 1,
        x: sx,
        y: sy,
        yCenter: sprite.yCenter,
      })
      const shadow = shadowSprite
        ? this.world.shadows.add({
            url: shadowSprite.url,
            width: shadowSprite.width || 1,
            height: shadowSprite.height || 1,
            yCenter: shadowSprite.yCenter,
            x: sx,
            y: sy,
          })
        : null
      this._active.push({
        projId: data.projId ?? null,
        spriteInst: s,
        shadowInst: shadow,
        item: resolvedItem,
        sx,
        sy,
        pdx: dx,
        pdy: dy,
        maxDist: dist,
        speed,
        elapsed: 0,
        impact,
        projRadius,
        arc,
        maxTargets,
        hitCount: 0,
        hitEntities: shooter ? new Set([shooter]) : new Set(),
        stopped: false,
        ex,
        ey,
      })
    }

    if (delay > 0) setTimeout(doSpawn, delay * 1000)
    else doSpawn()
  }

  _checkEntityHit(p) {
    const traveled = p.speed * p.elapsed
    const cx = p.sx + p.pdx * traveled
    const cy = p.sy + p.pdy * traveled
    const candidates = this.world.targets.query(cx, cy, p.projRadius)
    for (const entry of candidates) {
      if (p.hitEntities.has(entry.value)) continue
      if (!entry.value.isHitBy?.(p.item)) continue
      const totalR = p.projRadius + entry.radius
      const distSq = (entry.x - cx) ** 2 + (entry.y - cy) ** 2
      if (distSq > totalR * totalR) continue

      p.hitEntities.add(entry.value)
      p.hitCount++

      // play impact at hit point for piercing shots too
      if (p.impact && p.hitCount < p.maxTargets) {
        const impactSprite = this.world.sprites.add({
          url: p.impact.url,
          fps: p.impact.fps,
          width: p.impact.width || 1,
          height: p.impact.height || 1,
          x: cx,
          y: cy,
          yCenter: p.impact.yCenter,
          loop: false,
          onEnd: () => impactSprite.destroy(),
        })
      }

      if (p.hitCount >= p.maxTargets) {
        p.ex = cx
        p.ey = cy
        p.stopDist = traveled
        p.stopped = true
        return
      }
    }
  }

  stop(projId, x, y) {
    const p = this._active.find(p => p.projId === projId)
    if (!p || p.stopped) return
    p.ex = x
    p.ey = y
    p.stopDist = Math.sqrt((x - p.sx) ** 2 + (y - p.sy) ** 2)
    p.stopped = true
  }

  update(delta) {
    for (let i = this._active.length - 1; i >= 0; i--) {
      const p = this._active[i]
      p.elapsed += delta

      if (!p.stopped && !p.arc) this._checkEntityHit(p)

      const traveled = Math.min(p.speed * p.elapsed, p.maxDist)

      const x = p.sx + p.pdx * traveled
      const y = p.sy + p.pdy * traveled
      const arcOffset = p.arc ? p.arc * p.maxDist * Math.sin((traveled / p.maxDist) * Math.PI) : 0
      p.spriteInst.setPosition(x, y + arcOffset)
      p.shadowInst?.setPosition(x, y)
      if (p.arc) {
        const t = traveled / p.maxDist
        const arcDy = p.arc * Math.PI * Math.cos(t * Math.PI)
        p.spriteInst.setRotation(Math.atan2(p.pdy + arcDy, p.pdx))
      } else {
        p.spriteInst.setRotation(Math.atan2(p.pdy, p.pdx))
      }

      const done = p.stopped ? traveled >= p.stopDist : traveled >= p.maxDist
      if (done) {
        p.spriteInst.destroy()
        p.shadowInst?.destroy()
        if (p.impact) {
          const impactSprite = this.world.sprites.add({
            url: p.impact.url,
            fps: p.impact.fps,
            width: p.impact.width || 1,
            height: p.impact.height || 1,
            x: p.ex,
            y: p.ey,
            yCenter: p.impact.yCenter,
            loop: false,
            onEnd: () => impactSprite.destroy(),
          })
        }
        this._active.splice(i, 1)
      }
    }
  }
}
